import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Globe, Loader2, Search, Check, AlertTriangle, Clock, Link2,
  Pencil, Shield, RefreshCw, ExternalLink, Copy, Server
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl ${className}`} {...props}>{children}</div>
);

const STATUS_BADGES = {
  none: { label: 'No Custom Domain', color: '#64748B' },
  pending: { label: 'DNS Pending', color: '#F59E0B' },
  verified: { label: 'Verified', color: '#00CC99' },
  active: { label: 'Active', color: '#00CC99' },
  failed: { label: 'Failed', color: '#EF4444' },
};

export default function MasterDomains() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editDomain, setEditDomain] = useState(null);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('masterToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/master/domains/all`, { headers })
      .then(res => setDomains(res.data || []))
      .catch(() => toast.error('Failed to load domains'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = domains.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return d.site_name?.toLowerCase().includes(q) || d.subdomain?.toLowerCase().includes(q) || d.custom_domain?.toLowerCase().includes(q);
  });

  const onSubdomain = domains.filter(d => !d.custom_domain || d.custom_domain_status === 'none');
  const withCustom = domains.filter(d => d.custom_domain && d.custom_domain_status !== 'none');

  const handleSave = async () => {
    if (!editDomain) return;
    setSaving(true);
    try {
      await axios.put(`${API}/master/domains/${editDomain.site_id}`, {
        subdomain: editDomain.subdomain,
        custom_domain: editDomain.custom_domain,
        custom_domain_status: editDomain.custom_domain_status,
      }, { headers });
      setDomains(prev => prev.map(d => d.site_id === editDomain.site_id ? {
        ...d,
        subdomain: editDomain.subdomain,
        full_subdomain: `${editDomain.subdomain}.entreprenatives.com`,
        custom_domain: editDomain.custom_domain,
        custom_domain_status: editDomain.custom_domain_status,
      } : d));
      setEditDomain(null);
      toast.success('Domain config updated');
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const copySubdomain = (d) => {
    navigator.clipboard.writeText(d.full_subdomain || `${d.subdomain}.entreprenatives.com`);
    toast.success('Subdomain copied');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={32} className="animate-spin text-[#8B5CF6]" /></div>;

  return (
    <div className="space-y-6" data-testid="master-domains-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Domains & Subdomains</h1>
        <p className="text-sm text-slate-400 mt-1">Manage client domains, subdomains, and DNS status</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <GlassCard className="p-4 text-center">
          <Globe size={18} className="mx-auto mb-1 text-[#8B5CF6]" />
          <p className="text-xl font-bold text-white">{domains.length}</p>
          <p className="text-[10px] text-slate-500">Total Sites</p>
        </GlassCard>
        <GlassCard className="p-4 text-center">
          <Server size={18} className="mx-auto mb-1 text-[#3B82F6]" />
          <p className="text-xl font-bold text-white">{onSubdomain.length}</p>
          <p className="text-[10px] text-slate-500">On EntrepreNatives Subdomain</p>
        </GlassCard>
        <GlassCard className="p-4 text-center">
          <Link2 size={18} className="mx-auto mb-1 text-[#00CC99]" />
          <p className="text-xl font-bold text-white">{withCustom.length}</p>
          <p className="text-[10px] text-slate-500">Custom Domain</p>
        </GlassCard>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search domains..." className="pl-10 bg-white/5 border-white/10 text-white rounded-xl" data-testid="domain-search" />
      </div>

      <GlassCard className="overflow-hidden">
        <table className="w-full text-left text-sm" data-testid="domains-table">
          <thead><tr className="text-xs uppercase tracking-wider text-slate-400 border-b border-white/10">
            <th className="px-5 py-3 font-medium">Site</th>
            <th className="px-5 py-3 font-medium">Subdomain</th>
            <th className="px-5 py-3 font-medium">Custom Domain</th>
            <th className="px-5 py-3 font-medium">Status</th>
            <th className="px-5 py-3 font-medium">SSL</th>
            <th className="px-5 py-3 font-medium">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map(d => {
              const statusCfg = STATUS_BADGES[d.custom_domain_status || 'none'];
              return (
                <tr key={d.site_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-5 py-3.5 text-white font-medium">{d.site_name}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#A78BFA] font-mono">{d.full_subdomain || `${d.subdomain}.entreprenatives.com`}</span>
                      <button onClick={() => copySubdomain(d)} className="text-slate-600 hover:text-white"><Copy size={11} /></button>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-xs text-slate-400">{d.custom_domain || '—'}</td>
                  <td className="px-5 py-3.5">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: `${statusCfg.color}15`, color: statusCfg.color }}>{statusCfg.label}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`text-xs ${d.ssl_status === 'active' ? 'text-[#00CC99]' : 'text-slate-500'}`}>{d.ssl_status === 'active' ? 'Active' : d.ssl_status || 'N/A'}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <button onClick={() => setEditDomain({ ...d })} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-slate-300 hover:text-white transition-colors">
                      <Pencil size={11} /> Manage
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </GlassCard>

      {/* Edit Dialog */}
      <Dialog open={!!editDomain} onOpenChange={() => setEditDomain(null)}>
        <DialogContent className="bg-[#0E0E23] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle>Manage Domain — {editDomain?.site_name}</DialogTitle></DialogHeader>
          {editDomain && (
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-slate-400 text-xs">Subdomain (xxx.entreprenatives.com)</Label>
                <Input value={editDomain.subdomain || ''} onChange={(e) => setEditDomain(d => ({ ...d, subdomain: e.target.value }))} className="bg-white/5 border-white/10 text-white rounded-xl mt-1" data-testid="edit-subdomain" />
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Custom Domain</Label>
                <Input value={editDomain.custom_domain || ''} onChange={(e) => setEditDomain(d => ({ ...d, custom_domain: e.target.value }))} placeholder="example.com" className="bg-white/5 border-white/10 text-white rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Custom Domain Status</Label>
                <select value={editDomain.custom_domain_status || 'none'} onChange={(e) => setEditDomain(d => ({ ...d, custom_domain_status: e.target.value }))} className="w-full bg-[#1E1E3A] border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm mt-1">
                  <option value="none">No Custom Domain</option>
                  <option value="pending">DNS Pending</option>
                  <option value="verified">Verified</option>
                  <option value="active">Active</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setEditDomain(null)} className="flex-1 py-2.5 rounded-xl text-sm bg-white/5 border border-white/10 text-slate-300">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="save-domain-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
